import {MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {BusinessOnboarding} from '@/types';
import {BusinessOnboardingStatus} from '@/types';
import * as formatDate from '@/utils/date';

import InvitesSummary from './InvitesSummary';

export type InviteAction = 'approve' | 'deny' | 'resend';

interface InvitesTableProps {
  list: BusinessOnboarding[];
  onAction: (invite: BusinessOnboarding, action: InviteAction) => void;
  onEdit: (invite: BusinessOnboarding) => void;
}

const InvitesTable = ({list, onAction, onEdit}: InvitesTableProps) => {
  if (!list.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
        No invites found.
      </p>
    );
  }

  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-12 px-4">&nbsp;</Th>
          <Th className="px-4">Full Name</Th>
          <Th className="px-4">Business Name</Th>
          <Th className="px-4">Business Email</Th>
          <Th className="px-4">Status</Th>
          <Th className="px-4">Date</Th>
          <Th>&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {list.map((invite, index) => (
          <InvitesTableRow
            key={invite.id}
            invite={invite}
            index={index}
            onAction={onAction}
            onEdit={onEdit}
          />
        ))}
      </TBody>
    </Table>
  );
};

const InvitesTableRow = ({
  invite,
  index,
  onAction,
  onEdit,
}: {
  invite: BusinessOnboarding;
  index: number;
  onAction: InvitesTableProps['onAction'];
  onEdit: InvitesTableProps['onEdit'];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  const isApproved = invite.meta?.status === BusinessOnboardingStatus.APPROVED;

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td className="font-medium px-4">{invite.name}</Td>
        <Td className="px-4">{invite.businessName || '-'}</Td>
        <Td className="px-4">{invite.email}</Td>
        <Td className="px-4">{invite.meta?.status ?? '-'}</Td>
        <Td className="px-4">{formatDate.DDMonYYYY(invite.createdAt || '')}</Td>
        <Td className="px-4">
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessOnboardingManage}
          >
            <div className="flex items-center gap-2">
              {isApproved ? (
                <Button
                  colorScheme="cyan"
                  onClick={() => onAction(invite, 'resend')}
                  className="text-xs px-3 py-1 h-8"
                >
                  Resend
                </Button>
              ) : (
                <>
                  <Button
                    colorScheme="green"
                    onClick={() => onAction(invite, 'approve')}
                    className="text-xs px-3 py-1 h-8"
                  >
                    Approve
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={() => onAction(invite, 'deny')}
                    className="text-xs px-3 py-1 h-8"
                  >
                    Deny
                  </Button>
                </>
              )}
            </div>
          </PermissionsProvider>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colspan={7} className="p-0 m-0">
            <div className="flex py-5 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <InvitesSummary
                invite={invite}
                onEdit={onEdit}
                onDeny={(selected) => onAction(selected, 'deny')}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default InvitesTable;
